/**
 * Sprint 60.77 — DkTemplateFill
 *
 * Compact widget that lives on the Smart AND Raw Optimizer pages.
 * Flow:
 *   1. User exports their per-contest Entry template from DraftKings.
 *   2. Uploads it here — we parse + classify contests server-side.
 *   3. Clicks "Download Filled" → server takes the lineups the user just
 *      built in Smart or Raw and stuffs the template's reserved slots
 *      round-robin, returning a DK-ready CSV.
 *   4. User uploads the returned CSV to DraftKings.
 *
 * `source` prop = "smart" | "raw" — routes to the correct lineup bucket.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudArrowUp, DownloadSimple, X, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import AutoSenseBuild from "@/components/AutoSenseBuild";
import DkUploadCart from "@/components/DkUploadCart";
import { downloadDkCsv } from "@/lib/dkDownload";

const AUTO_KEY = (src) => `wp_autosense_${src}`;

export default function DkTemplateFill({ source = "smart" }) {
  const [template, setTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Sprint 86 — route lineups to the tournament they were TUNED for, and make
  // the user confirm it before the CSV is built.
  const [routing, setRouting] = useState(null);
  const [alloc, setAlloc] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [routingLoading, setRoutingLoading] = useState(false);
  // Sprint 87 — auto-sense multi-build. OFF by default; when ON it replaces
  // the single-pool routing flow entirely.
  const [autoSense, setAutoSense] = useState(() => {
    try { return localStorage.getItem(AUTO_KEY(source)) === "1"; } catch { return false; }
  });
  const toggleAutoSense = () => {
    setAutoSense((v) => {
      const next = !v;
      try { localStorage.setItem(AUTO_KEY(source), next ? "1" : "0"); } catch { /* private mode */ }
      return next;
    });
  };

  const loadRouting = async () => {
    setRoutingLoading(true);
    try {
      const r = await api.entryTemplateRouting(source);
      setRouting(r);
      setAlloc(Object.fromEntries((r.contests || []).map((c) => [c.contest_id, c.suggested_lineups])));
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      setRouting((prev) => ({
        ...(prev || {}),
        error: typeof detail === "string" && detail
          ? detail
          : `Build lineups in the ${label} Optimizer first, then check routing.`,
      }));
    } finally {
      setRoutingLoading(false);
    }
  };

  const confirmRouting = async () => {
    setConfirming(true);
    try {
      const r = await api.entryTemplateRoutingConfirm(source, alloc);
      toast.success(`Routing confirmed · ${r.total_assigned} lineups assigned`, {
        description: "The CSV will place each batch in the tournament it was tuned for.",
      });
      loadRouting();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm");
    } finally {
      setConfirming(false);
    }
  };
  const fileRef = React.useRef();

  const label = source === "raw" ? "Raw" : "Smart";

  useEffect(() => {
    api.entryTemplateGet()
      .then((r) => setTemplate(r?.template ? r.template : (r?.contests?.length ? r : null)))
      .catch(() => setTemplate(null));
  }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.entryTemplateUpload(file);
      setTemplate(res);
      const contests = res?.contests?.length || 0;
      const slots = res?.total_slots || res?.reserved_slots || (res?.contests || []).reduce((s, c) => s + (c.entry_count || 0), 0);
      toast.success(`Template loaded · ${contests} contest${contests === 1 ? "" : "s"} · ${slots} reserved slot${slots === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Template upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onClear = async () => {
    if (!confirm("Clear the loaded DK template?")) return;
    try {
      await api.entryTemplateDelete();
      setTemplate(null);
      toast.success("Template cleared");
    } catch {
      toast.error("Clear failed");
    }
  };

  const onDownload = async () => {
    if (!template) {
      toast.error("Upload a DK entry template first");
      return;
    }
    setDownloading(true);
    try {
      await downloadDkCsv(api.entryTemplateFillUrl(source), `warpaint_${source}_dk_entries.csv`);
      toast.success(`Downloaded · upload this CSV to DraftKings to enter contests`);
    } catch (err) {
      toast.error(err?.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const contests = template?.contests || [];
  const totalSlots = template?.total_slots || contests.reduce((s, c) => s + (c.entry_count || 0), 0);

  return (
    <section
      data-testid={`dk-template-fill-${source}`}
      className="border border-[#39FF14]/25 bg-gradient-to-br from-[#0a1a10] via-[#0b0b0b] to-[#0a0a0a] p-4 mt-4"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14]">DraftKings Lineup CSV</div>
          <div className="heading text-lg font-bold text-white">
            Upload DK Template · Download Filled <span className="text-zinc-500 text-sm font-normal">· from {label} Optimizer</span>
          </div>
        </div>
        {template && (
          <button
            type="button"
            onClick={onClear}
            data-testid={`dk-template-clear-${source}`}
            className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] flex items-center gap-1"
          >
            <X size={12} />
            Clear
          </button>
        )}
      </div>

      <div className="text-xs text-zinc-400 mb-3 leading-relaxed">
        <span className="text-[#39FF14] font-bold">Step 1 · Upload</span> — In DK: <span className="text-zinc-300">My Contests → Contest → Export Entries</span> to get the empty template, then drop it here.
        <br /><span className="text-[#39FF14] font-bold">Step 2 · Assign</span> — Send each build to one tournament at a time in the cart below, or use Download Filled CSV to fill every reserved slot from the current build in one shot.
        <br /><span className="text-[#39FF14] font-bold">Step 3 · Re-upload to DK</span> — Take the downloaded file back to DK and upload it to enter your contests.
      </div>

      {!template ? (
        <label className="block cursor-pointer border-2 border-dashed border-zinc-800 hover:border-[#39FF14] transition-colors p-6 text-center">
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={onUpload}
            disabled={uploading}
            data-testid={`dk-template-upload-input-${source}`}
          />
          <CloudArrowUp size={32} className="mx-auto text-zinc-600 mb-2" />
          <div className="text-sm text-white font-medium">
            {uploading ? "Uploading…" : "Upload DK Entry Template CSV"}
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            CSV from DK · Export Entries
          </div>
        </label>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 border border-zinc-800 bg-black px-3 py-2">
            <CheckCircle size={16} className="text-[#39FF14]" weight="fill" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-bold truncate">
                {template.filename || "Entry template loaded"}
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                {contests.length} contest{contests.length === 1 ? "" : "s"} · {totalSlots} reserved slot{totalSlots === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          {contests.length > 0 && (
            <div className="border border-zinc-900 bg-black max-h-40 overflow-y-auto">
              <table className="w-full text-[11px] mono">
                <thead className="bg-[#0e0e0e] text-zinc-500">
                  <tr className="text-left uppercase tracking-widest text-[9px]">
                    <th className="px-3 py-1.5">Contest</th>
                    <th className="px-3 py-1.5 text-right">Entries</th>
                    <th className="px-3 py-1.5 text-right">Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {contests.map((c) => (
                    <tr key={c.contest_id} className="border-t border-zinc-900">
                      <td className="px-3 py-1 text-zinc-300 truncate max-w-md">{c.contest_name || c.contest_id}</td>
                      <td className="px-3 py-1 text-right text-white">{c.entry_count}</td>
                      <td className="px-3 py-1 text-right text-zinc-400">${c.entry_fee ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 border border-zinc-800 bg-black/40 px-3 py-2">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] font-bold">
                Auto-sense &amp; multi-build
              </div>
              <div className="text-[10px] text-zinc-500 leading-snug max-w-xl">
                Read each tournament's real field size and max entries, build a separate portfolio with the
                tuning that matches it, and place every lineup in the contest it was built for.
              </div>
            </div>
            <button type="button" onClick={toggleAutoSense} aria-pressed={autoSense}
              data-testid={`auto-sense-toggle-${source}`}
              className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${autoSense
                ? "border-[#FFCC00] bg-[#FFCC00] text-black font-bold"
                : "border-zinc-700 text-zinc-400 hover:border-[#FFCC00] hover:text-[#FFCC00]"}`}>
              {autoSense ? "On" : "Off"}
            </button>
          </div>
          {autoSense && <AutoSenseBuild source={source} />}
          {!autoSense && (
          <>
          <DkUploadCart source={source} />
          <div className="border border-zinc-800 bg-black/40 p-2.5 mb-2" data-testid={`dk-routing-${source}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] font-bold">
                Tuning → tournament routing
              </span>
              <button type="button" onClick={loadRouting} disabled={routingLoading} data-testid={`dk-routing-check-${source}`}
                className="mono text-[9px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-300 hover:border-[#FFCC00] disabled:opacity-40">
                {routingLoading ? "Checking…" : "Check routing"}
              </button>
            </div>
            {!routing && !routingLoading && (
              <div className="mono text-[10px] text-zinc-500 mt-1" data-testid={`dk-routing-hint-${source}`}>
                Build lineups in the {label} Optimizer, then check routing to see which tournament each batch belongs in.
              </div>
            )}
            {routing?.error && (
              <div className="mono text-[10px] text-[#FF8A00] mt-1" data-testid={`dk-routing-error-${source}`}>
                {String(routing.error)}
              </div>
            )}
            {routing?.tuning && (
              <>
                <div className="mono text-[10px] text-zinc-400 mt-1">
                  This batch is tuned for{" "}
                  <span className="text-white font-bold">{routing.tuning.max_entries_per_user ?? "—"}-max</span>
                  {" · field "}
                  <span className="text-white font-bold">{(routing.tuning.field_size ?? 0).toLocaleString()}</span>
                  {" · "}{routing.tuning.num_lineups} lineups · {String(routing.tuning.slate_type || "").toUpperCase()}
                </div>
                {routing.warning && (
                  <div className="mono text-[10px] text-[#FF8A00] mt-1" data-testid={`dk-routing-warn-${source}`}>
                    {routing.warning}
                  </div>
                )}
                <div className="mt-2 space-y-1">
                  {(routing.contests || []).map((c) => (
                    <div key={c.contest_id} className="flex items-center gap-2 flex-wrap border-b border-zinc-900 pb-1"
                         data-testid={`dk-routing-row-${c.contest_id}`}>
                      <span className={`mono text-[9px] px-1 py-[1px] border ${c.fits_tuning ? "border-[#39FF14]/60 text-[#39FF14]" : "border-[#FF3B30]/60 text-[#FF3B30]"}`}>
                        {c.fits_tuning ? "fits" : "mismatch"}
                      </span>
                      <span className="text-[11px] text-zinc-200 truncate flex-1 min-w-[120px]">{c.contest_name}</span>
                      <span className="mono text-[9px] text-zinc-600">{c.entries_reserved} slots</span>
                      <input
                        type="number" min={0} max={c.entries_reserved}
                        value={alloc[c.contest_id] ?? 0}
                        onChange={(e) => setAlloc((a) => ({ ...a, [c.contest_id]: Math.max(0, Number(e.target.value) || 0) }))}
                        data-testid={`dk-routing-count-${c.contest_id}`}
                        className="w-16 bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-1.5 py-0.5 mono text-[11px] text-right text-zinc-100"
                      />
                      <span className="mono text-[9px] text-zinc-600 basis-full">{c.reason}</span>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={confirmRouting} disabled={confirming}
                  data-testid={`dk-routing-confirm-${source}`}
                  className="w-full mt-2 py-2 border border-[#FFCC00] text-[#FFCC00] font-bold uppercase tracking-widest text-[10px] hover:bg-[#FFCC00] hover:text-black disabled:opacity-40 transition-colors">
                  {confirming ? "Confirming…" : routing.confirmed ? "Re-confirm routing" : "Confirm this routing"}
                </button>
                {routing.confirmed && (
                  <div className="mono text-[9px] text-[#39FF14] mt-1" data-testid={`dk-routing-confirmed-${source}`}>
                    Routing confirmed — the CSV gives each tournament its own slice of the batch.
                  </div>
                )}
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            data-testid={`dk-template-download-${source}`}
            className="w-full bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 text-black font-black uppercase tracking-widest text-xs py-3 transition-colors flex items-center justify-center gap-2"
          >
            <DownloadSimple size={16} weight="bold" />
            {downloading ? "Building CSV…" : "Download Filled CSV"}
          </button>
          </>
          )}
        </div>
      )}
    </section>
  );
}
